import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, useEffect } from 'react';
import { Card, NO_ICON, NO_STICKER, PresetNameStyleMap, editionList, frameList } from '../../model';
import { Select, Input, InputNumber, Space, Popover, Checkbox, Dropdown, Menu, Radio, Tabs } from 'antd';
import {
    FileImageOutlined,
    EditOutlined,
    InfoCircleOutlined,
    ThunderboltOutlined,
    LinkOutlined,
    FontSizeOutlined,
    UnorderedListOutlined,
    UploadOutlined,
    DownloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useCard, useLanguage } from '../../service';
import { useI18N } from '../../service/use-i18n';
import { useShallow } from 'zustand/react/shallow';
import { checkMonster, checkXyz, checkLink } from '../../util';
import { CardInputPanelRef, CardInputPanel as CardInputPanelProps } from './index';
import {
    EffectInputGroup,
    EffectInputGroupRef,
} from './input-group';
import { InlineColorPicker } from '../../component';
import { getNameFontOptionList, StickerButtonList } from './const';
import { SimplifiedImageInput, SimplifiedImageInputRef } from './simplified-image-input';
import { CardSearch } from '../../component/card-search';
import { GalleryCartActions } from '../../component/gallery-cart-actions';
import { MobileTabScroller } from '../../component/mobile-tab-scroller';

const { TextArea } = Input;
const { TabPane } = Tabs;

// Card type presets with smart defaults - Unused, kept for reference
// const CARD_TYPE_PRESETS = [
//     {
//         label: 'Monster Cards',
//         options: [
//             { label: 'Normal Monster', value: 'normal', frame: 'normal', attribute: 'LIGHT', isPendulum: false, isLink: false },
//             { label: 'Effect Monster', value: 'effect', frame: 'effect', attribute: 'DARK', isPendulum: false, isLink: false },
//             { label: 'Ritual Monster', value: 'ritual', frame: 'ritual', attribute: 'LIGHT', isPendulum: false, isLink: false },
//             { label: 'Fusion Monster', value: 'fusion', frame: 'fusion', attribute: 'LIGHT', isPendulum: false, isLink: false },
//             { label: 'Synchro Monster', value: 'synchro', frame: 'synchro', attribute: 'LIGHT', isPendulum: false, isLink: false },
//             { label: 'XYZ Monster', value: 'xyz', frame: 'xyz', attribute: 'DARK', isPendulum: false, isLink: false },
//             { label: 'Link Monster', value: 'link', frame: 'link', attribute: 'DARK', isPendulum: false, isLink: true },
//             { label: 'Pendulum Effect Monster', value: 'pendulum-effect', frame: 'effect', attribute: 'DARK', isPendulum: true, isLink: false },
//             { label: 'Pendulum Normal Monster', value: 'pendulum-normal', frame: 'normal', attribute: 'LIGHT', isPendulum: true, isLink: false },
//             { label: 'Dark Synchro Monster', value: 'dark-synchro', frame: 'dark-synchro', attribute: 'DARK', isPendulum: false, isLink: false },
//         ],
//     },
//     {
//         label: 'Spell & Trap Cards',
//         options: [
//             { label: 'Spell Card', value: 'spell', frame: 'spell', attribute: 'SPELL', isPendulum: false, isLink: false },
//             { label: 'Trap Card', value: 'trap', frame: 'trap', attribute: 'TRAP', isPendulum: false, isLink: false },
//         ],
//     },
//     {
//         label: 'Other',
//         options: [
//             { label: 'Token', value: 'token', frame: 'token', attribute: 'LIGHT', isPendulum: false, isLink: false },
//         ],
//     },
// ];

// const ATTRIBUTE_OPTIONS = [
//     { label: '🌟 LIGHT', value: 'LIGHT' },
//     { label: '🌑 DARK', value: 'DARK' },
//     { label: '🔥 FIRE', value: 'FIRE' },
//     { label: '💧 WATER', value: 'WATER' },
//     { label: '💨 WIND', value: 'WIND' },
//     { label: '🌍 EARTH', value: 'EARTH' },
//     { label: '✨ DIVINE', value: 'DIVINE' },
// ];

const LEVEL_OPTIONS = Array.from({ length: 14 }, (_, i) => ({
    label: i === 0 ? '0' : `${'⭐'.repeat(i)} ${i}`,
    value: i,
}));

// Link arrow positions (8 directions) - Unused, kept for reference
// const LINK_ARROW_POSITIONS = [
//     { value: '1', label: '↖ Top-Left', position: 'top-left' },
//     { value: '2', label: '↑ Top', position: 'top' },
//     { value: '3', label: '↗ Top-Right', position: 'top-right' },
//     { value: '4', label: '← Left', position: 'left' },
//     { value: '6', label: '→ Right', position: 'right' },
//     { value: '7', label: '↙ Bottom-Left', position: 'bottom-left' },
//     { value: '8', label: '↓ Bottom', position: 'bottom' },
//     { value: '9', label: '↘ Bottom-Right', position: 'bottom-right' },
// ];

// const MONSTER_TYPE_OPTIONS = [
//     'Aqua', 'Beast', 'Beast-Warrior', 'Cyberse', 'Dinosaur', 'Divine-Beast',
//     'Dragon', 'Fairy', 'Fiend', 'Fish', 'Insect', 'Machine', 'Plant',
//     'Psychic', 'Pyro', 'Reptile', 'Rock', 'Sea Serpent', 'Spellcaster',
//     'Thunder', 'Warrior', 'Winged Beast', 'Wyrm', 'Zombie',
// ].map(type => ({ label: type, value: type }));

// const ABILITY_TYPE_OPTIONS = [
//     'Normal', 'Effect', 'Tuner', 'Flip', 'Gemini', 'Spirit', 'Toon', 'Union',
// ].map(type => ({ label: type, value: type }));

// const SPELL_TRAP_ICON_OPTIONS = [
//     { label: 'Normal', value: NO_ICON },
//     { label: 'Continuous', value: 'CONTINUOUS' },
//     { label: 'Quick-Play', value: 'QUICK-PLAY' },
//     { label: 'Counter', value: 'COUNTER' },
//     { label: 'Equip', value: 'EQUIP' },
//     { label: 'Field', value: 'FIELD' },
//     { label: 'Ritual', value: 'RITUAL' },
// ];

export const SimplifiedInputPanel = forwardRef<CardInputPanelRef, CardInputPanelProps>(({
    applyCardData,
    artworkCanvas,
    backgroundCanvas,
    onCropChange,
    onTainted,
    onSourceLoaded,
    onOpenSearch,
    onImport = () => {},
    allowHotkey = false,
    onDownload = () => {},
}, forwardedRef) => {
    const language = useLanguage();
    const { languageMetadataMap, languageInfo, changeLanguage } = useI18N(useShallow(({
        languageMetadataMap,
        languageInfo,
        changeLanguage,
    }) => ({
        languageMetadataMap,
        languageInfo,
        changeLanguage,
    })));

    const CARD_TYPE_OPTIONS_TRANSLATED = useMemo(() => [
        {
            label: language['input.card-type.group.monster'] || 'Monster Cards',
            options: [
                { label: language['input.frame.normal'] || 'Normal Monster', value: 'normal', frame: 'normal', attribute: 'LIGHT', isPendulum: false, isLink: false },
                { label: language['input.frame.effect'] || 'Effect Monster', value: 'effect', frame: 'effect', attribute: 'DARK', isPendulum: false, isLink: false },
                { label: language['input.frame.ritual'] || 'Ritual Monster', value: 'ritual', frame: 'ritual', attribute: 'LIGHT', isPendulum: false, isLink: false },
                { label: language['input.frame.fusion'] || 'Fusion Monster', value: 'fusion', frame: 'fusion', attribute: 'LIGHT', isPendulum: false, isLink: false },
                { label: language['input.frame.synchro'] || 'Synchro Monster', value: 'synchro', frame: 'synchro', attribute: 'LIGHT', isPendulum: false, isLink: false },
                { label: language['input.frame.xyz'] || 'XYZ Monster', value: 'xyz', frame: 'xyz', attribute: 'DARK', isPendulum: false, isLink: false },
                { label: language['input.frame.link'] || 'Link Monster', value: 'link', frame: 'link', attribute: 'DARK', isPendulum: false, isLink: true },
                { label: language['input.pendulum.label'] || 'Pendulum', value: 'pendulum', frame: 'effect', attribute: 'DARK', isPendulum: true, isLink: false },
                { label: language['input.frame.dark-synchro'] || 'Dark Synchro Monster', value: 'dark-synchro', frame: 'dark-synchro', attribute: 'DARK', isPendulum: false, isLink: false },
            ],
        },
        {
            label: language['input.card-type.group.spell-trap'] || 'Spell & Trap Cards',
            options: [
                { label: language['input.frame.spell'] || 'Spell Card', value: 'spell', frame: 'spell', attribute: 'SPELL', isPendulum: false, isLink: false },
                { label: language['input.frame.trap'] || 'Trap Card', value: 'trap', frame: 'trap', attribute: 'TRAP', isPendulum: false, isLink: false },
            ],
        },
        {
            label: language['input.card-type.group.other'] || 'Other',
            options: [
                { label: language['input.frame.token'] || 'Token', value: 'token', frame: 'token', attribute: 'LIGHT', isPendulum: false, isLink: false },
            ],
        },
    ], [language]);

    const FRAME_VARIANT_OPTIONS = useMemo(() => frameList.map(frameInfo => ({
        label: language[frameInfo.nameKey] || frameInfo.name,
        value: frameInfo.name,
    })), [language]);

    // Translated attribute options
    const ATTRIBUTE_OPTIONS_TRANSLATED = useMemo(() => [
        { label: `🌟 ${language['input.attribute.light'] || 'LIGHT'}`, value: 'LIGHT' },
        { label: `🌑 ${language['input.attribute.dark'] || 'DARK'}`, value: 'DARK' },
        { label: `🔥 ${language['input.attribute.fire'] || 'FIRE'}`, value: 'FIRE' },
        { label: `💧 ${language['input.attribute.water'] || 'WATER'}`, value: 'WATER' },
        { label: `💨 ${language['input.attribute.wind'] || 'WIND'}`, value: 'WIND' },
        { label: `🌍 ${language['input.attribute.earth'] || 'EARTH'}`, value: 'EARTH' },
        { label: `✨ ${language['input.attribute.divine'] || 'DIVINE'}`, value: 'DIVINE' },
    ], [language]);

    // Translated spell/trap icon options
    const SPELL_TRAP_ICON_OPTIONS_TRANSLATED = useMemo(() => [
        { label: language['input.icon.normal'] || 'Normal', value: NO_ICON },
        { label: language['input.icon.continuous'] || 'Continuous', value: 'CONTINUOUS' },
        { label: language['input.icon.quick-play'] || 'Quick-Play', value: 'QUICK-PLAY' },
        { label: language['input.icon.counter'] || 'Counter', value: 'COUNTER' },
        { label: language['input.icon.equip'] || 'Equip', value: 'EQUIP' },
        { label: language['input.icon.field'] || 'Field', value: 'FIELD' },
        { label: language['input.icon.ritual'] || 'Ritual', value: 'RITUAL' },
    ], [language]);

    const {
        frame,
        attribute,
        star,
        atk,
        def,
        name,
        // effect, // Unused
        isPendulum,
        pendulumFrame,
        isLink,
        pendulumEffect,
        pendulumScaleRed,
        pendulumScaleBlue,
        typeAbility,
        // cardIcon, // Unused
        subFamily,
        linkMap,
        linkRating,
        setId,
        password,
        creator,
        isFirstEdition,
        firstEditionText,
        hasCornerText,
        cornerText,
        sticker,
        foil,
        format,
        // effectTextStyle, // Unused
        nameStyleType,
        nameStyle,
        getUpdater,
        setCard,
    } = useCard(useShallow(({
        card: {
            frame, attribute, star, atk, def, name, effect,
            isPendulum, pendulumFrame, isLink, pendulumEffect, pendulumScaleRed, pendulumScaleBlue,
            typeAbility, cardIcon, subFamily, linkMap, linkRating,
            setId, password, creator, isFirstEdition, firstEditionText,
            hasCornerText, cornerText, sticker, foil, format,
            effectTextStyle, nameStyleType, nameStyle,
        },
        getUpdater,
        setCard,
    }) => ({
        frame, attribute, star, atk, def, name, effect,
        isPendulum, pendulumFrame, isLink, pendulumEffect, pendulumScaleRed, pendulumScaleBlue,
        typeAbility, cardIcon, subFamily, linkMap, linkRating,
        setId, password, creator, isFirstEdition, firstEditionText,
        hasCornerText, cornerText, sticker, foil, format,
        effectTextStyle, nameStyleType, nameStyle,
        getUpdater,
        setCard,
    })));

    const imageInputGroupRef = useRef<SimplifiedImageInputRef>(null);
    const effectInputGroupRef = useRef<EffectInputGroupRef>(null);

    // Responsive tab position
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Local state for inputs to prevent lag - updates immediately while debouncing the actual card state
    const [localName, setLocalName] = useState(name);
    const [localPassword, setLocalPassword] = useState(password);
    const [localSetId, setLocalSetId] = useState(setId);
    const [localCreator, setLocalCreator] = useState(creator);
    const [localFirstEdition, setLocalFirstEdition] = useState(firstEditionText);
    const [localCornerText, setLocalCornerText] = useState(cornerText);

    // Sync local state when card state changes externally
    useEffect(() => { setLocalName(name) }, [name]);
    useEffect(() => { setLocalPassword(password) }, [password]);
    useEffect(() => { setLocalSetId(setId) }, [setId]);
    useEffect(() => { setLocalCreator(creator) }, [creator]);
    useEffect(() => { setLocalFirstEdition(firstEditionText) }, [firstEditionText]);
    useEffect(() => { setLocalCornerText(cornerText) }, [cornerText]);

    const logSelectDropdownDebug = useCallback((label: string) => (open: boolean) => {
        if (!open) return;

        window.setTimeout(() => {
            const dropdowns = Array.from(document.querySelectorAll<HTMLElement>('.ant-select-dropdown'))
                .filter(dropdown => !dropdown.classList.contains('ant-select-dropdown-hidden'));
            const dropdown = dropdowns[dropdowns.length - 1];

            if (!dropdown) {
                console.warn(`[Yugi Select Debug] ${label}: no visible dropdown`);
                return;
            }

            const rect = dropdown.getBoundingClientRect();
            const style = window.getComputedStyle(dropdown);
            const holder = dropdown.querySelector<HTMLElement>('.rc-virtual-list-holder');
            const holderStyle = holder ? window.getComputedStyle(holder) : null;
            const item = dropdown.querySelector<HTMLElement>('.ant-select-item-option');
            const itemStyle = item ? window.getComputedStyle(item) : null;
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + Math.min(rect.height / 2, 24);
            const topElement = document.elementFromPoint(centerX, centerY) as HTMLElement | null;

            console.group(`[Yugi Select Debug] ${label}`);
            console.log('theme', {
                htmlClass: document.documentElement.className,
                bodyClass: document.body.className,
            });
            console.log('dropdown', {
                className: dropdown.className,
                rect: {
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                },
                background: style.background,
                backgroundColor: style.backgroundColor,
                color: style.color,
                opacity: style.opacity,
                position: style.position,
                zIndex: style.zIndex,
                overflow: style.overflow,
                pointerEvents: style.pointerEvents,
            });
            console.log('holder', holder && holderStyle ? {
                className: holder.className,
                background: holderStyle.background,
                backgroundColor: holderStyle.backgroundColor,
                opacity: holderStyle.opacity,
                zIndex: holderStyle.zIndex,
            } : null);
            console.log('first item', item && itemStyle ? {
                text: item.textContent,
                className: item.className,
                background: itemStyle.background,
                backgroundColor: itemStyle.backgroundColor,
                color: itemStyle.color,
                opacity: itemStyle.opacity,
                zIndex: itemStyle.zIndex,
            } : null);
            console.log('elementFromPoint', topElement ? {
                tag: topElement.tagName,
                className: topElement.className,
                text: topElement.textContent?.slice(0, 80),
            } : null);
            console.groupEnd();
        }, 0);
    }, []);

    // Create debounced updaters for text inputs to reduce re-renders
    const debouncedNameUpdater = useMemo(() => getUpdater('name', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedPasswordUpdater = useMemo(() => getUpdater('password', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedSetIdUpdater = useMemo(() => getUpdater('setId', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedCreatorUpdater = useMemo(() => getUpdater('creator', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedFirstEditionUpdater = useMemo(() => getUpdater('firstEditionText', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedCornerTextUpdater = useMemo(() => getUpdater('cornerText', undefined, { type: 'debounce', wait: 100 }), [getUpdater]);
    const debouncedPendulumEffectUpdater = useMemo(() => getUpdater('pendulumEffect', undefined, 'debounce'), [getUpdater]);

    const isMonster = checkMonster({ frame });
    const isSpellTrap = frame === 'spell' || frame === 'trap';
    const isXyz = checkXyz({ frame });
    const isLinkMonster = checkLink({ isLink });
    const isDarkSynchro = frame === 'dark-synchro';

    // Determine current card type preset
    const currentCardType = useMemo(() => {
        if (isPendulum) return 'pendulum';
        if (isLink) return 'link';
        if (frame === 'dark-synchro') return 'dark-synchro';
        if (['normal', 'ritual', 'fusion', 'synchro', 'xyz', 'spell', 'trap', 'token'].includes(frame)) return frame;
        return 'effect';
    }, [frame, isPendulum, isLink]);

    const currentFrameVariant = useMemo(() => (
        isPendulum && pendulumFrame !== 'auto' ? pendulumFrame : frame
    ), [frame, isPendulum, pendulumFrame]);

    const currentFrameVariantOptions = useMemo(() => {
        if (!isPendulum || FRAME_VARIANT_OPTIONS.some(option => option.value === 'dl-skill')) return FRAME_VARIANT_OPTIONS;
        return [
            ...FRAME_VARIANT_OPTIONS,
            { label: 'Duel Links Skill', value: 'dl-skill' },
        ];
    }, [FRAME_VARIANT_OPTIONS, isPendulum]);

    // Get star/rank label based on card type
    const getStarLabel = () => {
        if (isXyz) return 'Rank';
        if (isDarkSynchro) return 'Negative Level';
        return 'Level';
    };

    // Handle card type change
    const handleCardTypeChange = useCallback((value: string) => {
        const preset = CARD_TYPE_OPTIONS_TRANSLATED
            .flatMap(group => group.options)
            .find(opt => opt.value === value);

        if (preset) {
            setCard(card => {
                const nextFrame = preset.isPendulum && checkMonster({ frame: card.frame })
                    ? card.frame
                    : preset.frame;
                const updates: Partial<Card> = {
                    frame: nextFrame,
                    attribute: preset.attribute,
                    isPendulum: preset.isPendulum,
                    isLink: preset.isLink,
                    pendulumFrame: preset.isPendulum ? nextFrame : 'auto',
                    pendulumRightFrame: 'auto',
                    starAlignment: 'auto', // Let canvas auto-determine based on frame
                    cardIcon: 'auto', // Let canvas auto-determine icon type based on frame
                };

                // Link monsters: clear DEF, ensure linkMap exists
                if (preset.isLink) {
                    updates.def = '';
                    updates.linkMap = card.linkMap.length > 0 ? card.linkMap : [];
                    updates.linkRating = card.linkRating || '';
                } else {
                    updates.linkMap = [];
                    updates.linkRating = '';
                    // Restore DEF if it was cleared
                    if (card.def === '' && checkMonster({ frame: nextFrame })) {
                        updates.def = '0';
                    }
                }

                // Pendulum: ensure pendulum fields exist
                if (preset.isPendulum) {
                    updates.pendulumEffect = card.pendulumEffect || '';
                    updates.pendulumScaleRed = card.pendulumScaleRed || '4';
                    updates.pendulumScaleBlue = card.pendulumScaleBlue || '4';
                } else {
                    updates.pendulumEffect = '';
                }

                // Token: clear password
                if (nextFrame === 'token') {
                    updates.password = '';
                    updates.star = 0;
                }

                // Spell/Trap: clear monster-specific fields and reset typeAbility
                if (nextFrame === 'spell' || nextFrame === 'trap') {
                    updates.star = 0;
                    updates.atk = '';
                    updates.def = '';
                    // Completely replace typeAbility array for spell/trap
                    updates.typeAbility = nextFrame === 'spell' ? ['Spell Card'] : ['Trap Card'];
                    updates.subFamily = NO_ICON; // Default to normal (no icon)
                } else if (checkMonster({ frame: nextFrame })) {
                    // Monster: ensure ATK/DEF exist and restore monster typeAbility if needed
                    if (card.atk === '') updates.atk = '0';
                    if (card.def === '' && !preset.isLink) updates.def = '0';
                    // If switching from spell/trap to monster, reset typeAbility
                    if (card.typeAbility.length === 1 && (card.typeAbility[0] === 'Spell Card' || card.typeAbility[0] === 'Trap Card')) {
                        updates.typeAbility = ['Warrior', 'Effect'];
                    }
                }

                return { ...card, ...updates };
            });
        }
    }, [setCard, CARD_TYPE_OPTIONS_TRANSLATED]);

    const handleFrameVariantChange = useCallback((value: string) => {
        setCard(card => {
            const nextIsLink = value === 'link';
            const updates: Partial<Card> = {
                frame: value,
                isLink: nextIsLink,
                starAlignment: 'auto',
                cardIcon: 'auto',
            };

            if (card.isPendulum) {
                updates.pendulumFrame = value;
                updates.pendulumRightFrame = 'auto';
                updates.pendulumEffect = card.pendulumEffect || '';
                updates.pendulumScaleRed = card.pendulumScaleRed || '4';
                updates.pendulumScaleBlue = card.pendulumScaleBlue || '4';
            }

            if (nextIsLink) {
                updates.def = '';
                updates.linkMap = card.linkMap.length > 0 ? card.linkMap : [];
                updates.linkRating = card.linkRating || '';
            } else {
                updates.linkMap = [];
                updates.linkRating = '';
                if (card.def === '' && checkMonster({ frame: value })) updates.def = '0';
            }

            if (value === 'spell' || value === 'trap' || value === 'speed-skill') {
                updates.star = 0;
                updates.atk = '';
                updates.def = '';
                updates.typeAbility = value === 'trap' ? ['Trap Card'] : ['Spell Card'];
                updates.subFamily = NO_ICON;
            } else if (checkMonster({ frame: value })) {
                if (card.atk === '') updates.atk = '0';
                if (card.def === '' && !nextIsLink) updates.def = '0';
                if (card.typeAbility.length === 1 && (card.typeAbility[0] === 'Spell Card' || card.typeAbility[0] === 'Trap Card')) {
                    updates.typeAbility = ['Warrior', 'Effect'];
                }
            }

            if (value === 'token') {
                updates.password = '';
                updates.star = 0;
            }

            return { ...card, ...updates };
        });
    }, [setCard]);

    // Generate random password
    const generatePassword = useCallback(() => {
        const randomPassword = Math.floor(10000000 + Math.random() * 90000000).toString();
        setLocalPassword(randomPassword);
        getUpdater('password')(randomPassword);
    }, [getUpdater]);

    // Generate random set ID
    const generateSetId = useCallback(() => {
        const formats = format === 'ocg' ? 'JP' : 'EN';
        const randomNum = Math.floor(1 + Math.random() * 999).toString().padStart(3, '0');
        const randomSetId = `YGOC-${formats}${randomNum}`;
        setLocalSetId(randomSetId);
        getUpdater('setId')(randomSetId);
    }, [format, getUpdater]);

    useImperativeHandle(forwardedRef, () => ({
        forceCardData: (card: Card) => {
            // DON'T call setCard here - it was already called in treatNewCard
            // Calling it again prevents the canvas from re-rendering because the effect value is the same
            // setCard(card);
            // Force the effect input to update to match the imported card
            effectInputGroupRef.current?.setValue(card.effect);
        },
        isLoading: () => {
            return imageInputGroupRef.current?.isLoading() ?? false;
        },
        requestImport: (mode: 'replace' | 'merge' | 'new') => {
            // Import is now inline in the tab, no modal to trigger
            // This is kept for backward compatibility with hotkeys
        },
    }));

    return (
        <div className="simplified-input-panel">
            {/* Card Type Selection & Quick Import - Always Visible */}
            <div className="card-type-section">
                <h3 className="section-title">
                    <ThunderboltOutlined /> {language['input.card-type.label'] || 'Select Card Type'}
                </h3>
                <Select
                    className="card-type-selector"
                    value={currentCardType}
                    onChange={handleCardTypeChange}
                    onDropdownVisibleChange={logSelectDropdownDebug('Select Card Type')}
                    options={CARD_TYPE_OPTIONS_TRANSLATED}
                    size="large"
                    style={{ width: '100%' }}
                    placeholder={language['input.card-type.placeholder'] || 'Choose what type of card to create...'}
                />
                
                {/* Quick Import Search */}
                <div style={{ marginTop: '16px' }}>
                    <h3 className="section-title" style={{ fontSize: '14px', marginBottom: '8px' }}>
                        <SearchOutlined /> {language['prompt.import.quick.label'] || 'Quick Import'}
                    </h3>
                    <CardSearch
                        onSelect={async (card: any) => {
                            try {
                                const { ygoproDeckToYgoCarderData } = await import('../../util/codec-ygoprodeck');
                                const { result } = ygoproDeckToYgoCarderData(card);
                                const { decodeCard } = await import('../../service');
                                const { card: decodedCard } = decodeCard(result, undefined);
                                
                                onImport(decodedCard, {
                                    forcePurityCheck: true,
                                    writeOnCurrentCard: true,
                                    addToList: false,
                                });
                                
                                const { notification } = await import('antd');
                                notification.info({
                                    message: language['prompt.import.cors.info.message'] || 'Card imported successfully',
                                    description: language['prompt.import.cors.info.description'] || 'Note: To download this card, right-click on it and select "Save image as..." due to image hosting restrictions.',
                                    duration: 6,
                                });
                            } catch (error) {
                                console.error('Import error:', error);
                            }
                        }}
                        placeholder={language['prompt.import.quick.placeholder'] || 'Search and import a card...'}
                        disabled={false}
                    />
                </div>
            </div>

            {/* Tabbed Content with Action Buttons */}
            <MobileTabScroller isMobile={isMobile}>
            <div className="tabs-with-actions">
                <Tabs defaultActiveKey="text" className="input-tabs" size="large" tabPosition={isMobile ? 'top' : 'left'}>
                <TabPane tab={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><EditOutlined /> {language['input.tab.text'] || 'Text'}</span>} key="text">
                    <div className="tab-content">
                        {/* Basic Information Section */}
                        <div className="input-section">
                            <h3 className="section-title">
                                <InfoCircleOutlined /> {language['input.basic-info.label'] || 'Basic Information'}
                            </h3>

                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                {/* Card Name - Full Width */}
                                <div className="input-field">
                                    <label>{language['input.name.label'] || 'Card Name'}</label>
                                    <Input
                                        value={localName}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setLocalName(newValue);
                                            debouncedNameUpdater(newValue);
                                        }}
                                        placeholder={language['input.name.placeholder'] || 'Enter card name'}
                                        size="large"
                                    />
                                </div>

                                <div className="input-field">
                                    <label>{isPendulum ? 'Pendulum Frame / Variant' : 'Frame / Variant'}</label>
                                    <Select
                                        value={currentFrameVariant}
                                        onChange={handleFrameVariantChange}
                                        onDropdownVisibleChange={logSelectDropdownDebug('Frame / Variant')}
                                        options={currentFrameVariantOptions}
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </div>

                                {/* Name Styling - 2 Column Layout */}
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FontSizeOutlined /> {language['input.name-style.label'] || 'Name Style'}
                                    </label>
                                    <div className="input-row">
                                        {/* Name Style Type Selector */}
                                        <div className="input-field" style={{ flex: 1 }}>
                                            <Select
                                                value={nameStyleType}
                                                onDropdownVisibleChange={logSelectDropdownDebug('Name Style')}
                                                onChange={(value) => {
                                                    setCard(card => {
                                                        const updates: Partial<Card> = { nameStyleType: value };

                                                        // If switching to predefined, apply the preset
                                                        if (value === 'predefined' && card.nameStyle.preset) {
                                                            const presetValue = PresetNameStyleMap[card.nameStyle.preset]?.value || {};
                                                            updates.nameStyle = { ...card.nameStyle, ...presetValue };
                                                        }

                                                        return { ...card, ...updates };
                                                    });
                                                }}
                                                options={[
                                                    { label: language['input.name-style.type.auto.label'] || 'Auto (Default)', value: 'auto' },
                                                    { label: language['input.name-style.type.predefined.label'] || 'Predefined Style', value: 'predefined' },
                                                    { label: language['input.name-style.type.custom.label'] || 'Custom Style', value: 'custom' },
                                                ]}
                                                style={{ width: '100%' }}
                                            />
                                        </div>

                                        {/* Predefined Style Selector */}
                                        {nameStyleType === 'predefined' && (
                                            <div className="input-field" style={{ flex: 1 }}>
                                                <Select
                                                    value={nameStyle.preset || 'commonB'}
                                                    onDropdownVisibleChange={logSelectDropdownDebug('Name Style Preset')}
                                                    onChange={(presetKey) => {
                                                        const presetValue = PresetNameStyleMap[presetKey]?.value || {};
                                                        setCard(card => ({
                                                            ...card,
                                                            nameStyle: { ...card.nameStyle, ...presetValue, preset: presetKey },
                                                        }));
                                                    }}
                                                    options={Object.values(PresetNameStyleMap).map(preset => ({
                                                        label: preset.label,
                                                        value: preset.key,
                                                    }))}
                                                    style={{ width: '100%' }}
                                                    placeholder={language['input.name-style.preset.placeholder'] || 'Select a preset style'}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Custom Style Options */}
                                    {nameStyleType === 'custom' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                                            {/* Font and Text Color - 2 Columns */}
                                            <div className="input-row">
                                                <div className="input-field" style={{ flex: 1 }}>
                                                    <label>{language['input.name-style.font.label'] || 'Font'}</label>
                                                    <Select
                                                        value={nameStyle.font || 'Auto'}
                                                        onDropdownVisibleChange={logSelectDropdownDebug('Name Style Font')}
                                                        onChange={(font) => {
                                                            setCard(card => ({
                                                                ...card,
                                                                nameStyle: { ...card.nameStyle, font },
                                                            }));
                                                        }}
                                                        options={getNameFontOptionList(language)}
                                                        style={{ width: '100%' }}
                                                        showSearch
                                                    />
                                                </div>
                                                <div className="input-field" style={{ flex: 1 }}>
                                                    <label>{language['input.name-style.color.text.label'] || 'Text Color'}</label>
                                                    <Popover
                                                        content={
                                                            <div style={{ padding: '8px' }}>
                                                                <InlineColorPicker
                                                                    value={nameStyle.fillStyle || '#000000'}
                                                                    onChange={(color) => {
                                                                        setCard(card => ({
                                                                            ...card,
                                                                            nameStyle: { ...card.nameStyle, fillStyle: color },
                                                                        }));
                                                                    }}
                                                                />
                                                            </div>
                                                        }
                                                        trigger="click"
                                                        placement="bottom"
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            padding: '8px 12px',
                                                            border: '1px solid var(--sub-level-1)',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            background: 'var(--main-level-2)',
                                                        }}>
                                                            <div style={{
                                                                width: '24px',
                                                                height: '24px',
                                                                borderRadius: '4px',
                                                                background: nameStyle.fillStyle || '#000000',
                                                                border: '1px solid var(--border-light)',
                                                            }} />
                                                            <span>{nameStyle.fillStyle || '#000000'}</span>
                                                        </div>
                                                    </Popover>
                                                </div>
                                            </div>

                                            {/* Outline Toggle and Settings */}
                                            <div className="input-field">
                                                <Checkbox
                                                    checked={nameStyle.hasOutline || false}
                                                    onChange={(e) => {
                                                        setCard(card => ({
                                                            ...card,
                                                            nameStyle: {
                                                                ...card.nameStyle,
                                                                hasOutline: e.target.checked,
                                                                lineWidth: e.target.checked ? (card.nameStyle.lineWidth || 3) : 0,
                                                                lineColor: card.nameStyle.lineColor || '#000000',
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    {language['input.name-style.outline.toggle.label'] || 'Enable Outline'}
                                                </Checkbox>
                                            </div>
                                            {nameStyle.hasOutline && (
                                                <div className="input-row">
                                                    <div className="input-field" style={{ flex: 1 }}>
                                                        <label>{language['input.name-style.slider.thickness.label'] || 'Outline Width'}</label>
                                                        <InputNumber
                                                            value={typeof nameStyle.lineWidth === 'number' ? nameStyle.lineWidth : 3}
                                                            onChange={(value) => {
                                                                setCard(card => ({
                                                                    ...card,
                                                                    nameStyle: { ...card.nameStyle, lineWidth: Number(value) || 0 },
                                                                }));
                                                            }}
                                                            min={0}
                                                            max={20}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                    <div className="input-field" style={{ flex: 1 }}>
                                                        <label>{language['input.name-style.outline.label'] || 'Outline Color'}</label>
                                                        <Popover
                                                            content={
                                                                <div style={{ padding: '8px' }}>
                                                                    <InlineColorPicker
                                                                        value={nameStyle.lineColor || '#000000'}
                                                                        onChange={(color) => {
                                                                            setCard(card => ({
                                                                                ...card,
                                                                                nameStyle: { ...card.nameStyle, lineColor: color },
                                                                            }));
                                                                        }}
                                                                    />
                                                                </div>
                                                            }
                                                            trigger="click"
                                                            placement="bottom"
                                                        >
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '8px 12px',
                                                                border: '1px solid var(--sub-level-1)',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                background: 'var(--main-level-2)',
                                                            }}>
                                                                <div style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    borderRadius: '4px',
                                                                    background: nameStyle.lineColor || '#000000',
                                                                    border: '1px solid var(--border-light)',
                                                                }} />
                                                            </div>
                                                        </Popover>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Monster-specific fields */}
                                {isMonster && (
                                    <>
                                        <div className="input-row">
                                            <div className="input-field" style={{ flex: 1 }}>
                                                <label>{language['input.attribute.label'] || 'Attribute'}</label>
                                                <Select
                                                    value={attribute}
                                                    onChange={(value) => getUpdater('attribute')(value as string)}
                                                    onDropdownVisibleChange={logSelectDropdownDebug('Attribute')}
                                                    options={ATTRIBUTE_OPTIONS_TRANSLATED}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div className="input-field" style={{ flex: 1 }}>
                                                <label>
                                                    {isLinkMonster ? (language['input.link.label'] || 'Link Rating') : getStarLabel()}
                                                </label>
                                                {isLinkMonster ? (
                                                    <Input
                                                        value={linkRating}
                                                        onChange={(e) => getUpdater('linkRating')(e.target.value)}
                                                        placeholder={language['input.link.auto.tooltip'] || 'Auto (calculated from arrows)'}
                                                    />
                                                ) : (
                                                    <Select
                                                        value={star}
                                                        onChange={(value) => getUpdater('star')(value as number)}
                                                        onDropdownVisibleChange={logSelectDropdownDebug('Level')}
                                                        options={LEVEL_OPTIONS}
                                                        style={{ width: '100%' }}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Link Arrows (only for Link monsters) */}
                                        {isLinkMonster && (
                                            <div className="input-field">
                                                <label>
                                                    <LinkOutlined /> {language['link-picker.label'] || 'Link Arrows'}
                                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--main-text-secondary)' }}>
                                                        ({language['input.link.select-arrows'] || 'Select arrow directions'})
                                                    </span>
                                                </label>
                                                <div className="link-arrow-grid" style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                                    gap: '8px',
                                                    padding: '12px',
                                                    background: 'var(--main-level-2)',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--sub-level-1)'
                                                }}>
                                                    {/* Top row */}
                                                    <Checkbox
                                                        checked={linkMap.includes('1')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '1']
                                                                : linkMap.filter(v => v !== '1');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↖
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={linkMap.includes('2')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '2']
                                                                : linkMap.filter(v => v !== '2');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↑
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={linkMap.includes('3')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '3']
                                                                : linkMap.filter(v => v !== '3');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↗
                                                    </Checkbox>

                                                    {/* Middle row */}
                                                    <Checkbox
                                                        checked={linkMap.includes('4')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '4']
                                                                : linkMap.filter(v => v !== '4');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ←
                                                    </Checkbox>
                                                    <div style={{ textAlign: 'center', padding: '8px', color: 'var(--main-text-secondary)' }}>
                                                        <LinkOutlined style={{ fontSize: '20px' }} />
                                                    </div>
                                                    <Checkbox
                                                        checked={linkMap.includes('6')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '6']
                                                                : linkMap.filter(v => v !== '6');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        →
                                                    </Checkbox>

                                                    {/* Bottom row */}
                                                    <Checkbox
                                                        checked={linkMap.includes('7')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '7']
                                                                : linkMap.filter(v => v !== '7');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↙
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={linkMap.includes('8')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '8']
                                                                : linkMap.filter(v => v !== '8');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↓
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={linkMap.includes('9')}
                                                        onChange={(e) => {
                                                            const newMap = e.target.checked
                                                                ? [...linkMap, '9']
                                                                : linkMap.filter(v => v !== '9');
                                                            getUpdater('linkMap')(newMap);
                                                        }}
                                                    >
                                                        ↘
                                                    </Checkbox>
                                                </div>
                                                <div style={{
                                                    marginTop: '8px',
                                                    fontSize: '12px',
                                                    color: 'var(--main-text-secondary)',
                                                    textAlign: 'center'
                                                }}>
                                                    {language['input.link.selected'] || 'Selected'}: {linkMap.length} {language['input.link.arrow'] || 'arrow'}{linkMap.length !== 1 ? 's' : ''}
                                                    {linkMap.length > 0 && ` (Link-${linkMap.length})`}
                                                </div>
                                            </div>
                                        )}

                                        <div className="input-field">
                                            <label>{language['input.type-ability.label'] || 'Type & Ability'}</label>
                                            <Input
                                                value={typeAbility.join(' / ')}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const newTypeAbility = value.split('/').map(s => s.trim()).filter(s => s.length > 0);
                                                    getUpdater('typeAbility')(newTypeAbility.length > 0 ? newTypeAbility : ['']);
                                                }}
                                                placeholder={language['input.type-ability.placeholder'] || 'e.g., Warrior / Effect / Tuner'}
                                                style={{ width: '100%' }}
                                            />
                                        </div>

                                        {isLinkMonster ? (
                                            <div className="input-field">
                                                <label>{language['input.atk.label'] || 'ATK'}</label>
                                                <Input
                                                    value={atk}
                                                    onChange={(e) => getUpdater('atk')(e.target.value)}
                                                    placeholder="0"
                                                />
                                            </div>
                                        ) : (
                                            <div className="input-row">
                                                <div className="input-field" style={{ flex: 1 }}>
                                                    <label>{language['input.atk.label'] || 'ATK'}</label>
                                                    <Input
                                                        value={atk}
                                                        onChange={(e) => getUpdater('atk')(e.target.value)}
                                                        placeholder="0"
                                                    />
                                                </div>
                                                <div className="input-field" style={{ flex: 1 }}>
                                                    <label>{language['input.def.label'] || 'DEF'}</label>
                                                    <Input
                                                        value={def}
                                                        onChange={(e) => getUpdater('def')(e.target.value)}
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Spell/Trap specific fields */}
                                {isSpellTrap && (
                                    <>
                                        <div className="input-field">
                                            <label>{language['input.type.st.label'] || 'Card Type Text'}</label>
                                            <Input
                                                value={typeAbility[0] || ''}
                                                onChange={(e) => {
                                                    const newTypeAbility = [e.target.value];
                                                    getUpdater('typeAbility')(newTypeAbility);
                                                }}
                                                placeholder={frame === 'spell' ? (language['input.attribute.spell'] || 'Spell Card') : (language['input.attribute.trap'] || 'Trap Card')}
                                            />
                                        </div>
                                        <div className="input-field">
                                            <label>{language['input.icon.label'] || 'Card Icon'}</label>
                                            <Select
                                                value={subFamily}
                                                onChange={(value) => getUpdater('subFamily')(value as string)}
                                                options={SPELL_TRAP_ICON_OPTIONS_TRANSLATED}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </>
                                )}
                            </Space>
                        </div>

                        {/* Card Text & Effects Section */}
                        <div className="input-section">
                            <h3 className="section-title">
                                <EditOutlined /> {language['input.card-text.label'] || 'Card Text & Effects'}
                            </h3>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                {/* Pendulum Effect (if pendulum) */}
                                {isPendulum && (
                                    <>
                                        <div className="input-field">
                                            <label>{language['input.pendulum.label'] || 'Pendulum Effect'}</label>
                                            <TextArea
                                                value={pendulumEffect}
                                                onChange={(e) => debouncedPendulumEffectUpdater(e.target.value)}
                                                placeholder={language['input.pendulum.placeholder'] || 'Enter pendulum effect'}
                                                rows={3}
                                            />
                                        </div>
                                        <div className="input-row">
                                            <div className="input-field" style={{ flex: 1 }}>
                                                <label>{language['input.scale.red.label'] || 'Scale (Red)'}</label>
                                                <Input
                                                    value={pendulumScaleRed}
                                                    onChange={(e) => getUpdater('pendulumScaleRed')(e.target.value)}
                                                />
                                            </div>
                                            <div className="input-field" style={{ flex: 1 }}>
                                                <label>{language['input.scale.blue.label'] || 'Scale (Blue)'}</label>
                                                <Input
                                                    value={pendulumScaleBlue}
                                                    onChange={(e) => getUpdater('pendulumScaleBlue')(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Main Effect */}
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>{isMonster ? (language['input.effect.monster.label'] || 'Monster Effect') : (language['input.effect.label'] || 'Card Effect')}</span>
                                    </label>
                                    <EffectInputGroup
                                        ref={effectInputGroupRef}
                                    />
                                </div>
                            </Space>
                        </div>
                    </div>
                </TabPane>

                <TabPane tab={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileImageOutlined /> {language['input.tab.artwork'] || 'Artwork'}</span>} key="artwork" forceRender={true}>
                    <div className="tab-content">
                        <SimplifiedImageInput
                            ref={imageInputGroupRef}
                            receivingCanvas={artworkCanvas}
                            onCropChange={onCropChange}
                            onTainted={onTainted}
                            onSourceLoaded={onSourceLoaded}
                        />
                    </div>
                </TabPane>

                <TabPane tab={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><UploadOutlined /> {language['input.tab.import'] || 'Import'}</span>} key="import">
                    <div className="tab-content">
                        <SimplifiedImportTab
                            language={language}
                            onImport={onImport}
                            allowHotkey={allowHotkey}
                        />
                    </div>
                </TabPane>

                <TabPane tab={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><ThunderboltOutlined /> {language['input.tab.advanced'] || 'Advanced'}</span>} key="advanced">
                    <div className="tab-content">
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                            {/* Language and Foil - 2 Columns */}
                            <div className="input-row">
                                <div className="input-field" style={{ flex: 1 }}>
                                    <label>{language['setting.option.language.label'] || 'Language'} / 言語</label>
                                    <Select
                                        value={languageInfo.codeName}
                                        onChange={(value) => changeLanguage(value)}
                                        options={Object.values(languageMetadataMap)
                                            .filter(meta => meta.active)
                                            .sort((a, b) => a.order - b.order)
                                            .map(meta => ({
                                                label: `${meta.name} (${meta.formalName})`,
                                                value: meta.codeName,
                                            }))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="input-field" style={{ flex: 1 }}>
                                    <label>{language['input.foil.label'] || 'Foil'}</label>
                                    <Select
                                        value={foil}
                                        onChange={(value) => getUpdater('foil')(value as string)}
                                        options={[
                                            { label: language['input.foil.normal.label'] || 'Normal', value: 'normal' },
                                            { label: language['input.foil.gold.label'] || 'Gold', value: 'gold' },
                                            { label: language['input.foil.platinum.label'] || 'Platinum', value: 'platinum' },
                                            { label: language['input.foil.rainbow.label'] || 'Rainbow', value: 'rainbow' },
                                        ]}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div className="input-row">
                                <div className="input-field" style={{ flex: 1 }}>
                                    <label>{language['input.set-id.label'] || 'Set ID'}</label>
                                    <Input.Group compact style={{ display: 'flex' }}>
                                        <Input
                                            value={localSetId}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setLocalSetId(newValue);
                                                debouncedSetIdUpdater(newValue);
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                        <span
                                            onClick={generateSetId}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 12px',
                                                background: 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))',
                                                border: '2px solid var(--main-border, rgba(255, 255, 255, 0.1))',
                                                borderLeft: 'none',
                                                borderRadius: '0 8px 8px 0',
                                                fontSize: '20px',
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--main-primary, rgba(24, 144, 255, 0.2))';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))';
                                            }}
                                        >
                                            🎲
                                        </span>
                                    </Input.Group>
                                </div>
                                <div className="input-field" style={{ flex: 1 }}>
                                    <label>{language['input.password.label'] || 'Password'}</label>
                                    <Input.Group compact style={{ display: 'flex' }}>
                                        <Input
                                            value={localPassword}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setLocalPassword(newValue);
                                                debouncedPasswordUpdater(newValue);
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                        <span
                                            onClick={generatePassword}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 12px',
                                                background: 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))',
                                                border: '2px solid var(--main-border, rgba(255, 255, 255, 0.1))',
                                                borderLeft: 'none',
                                                borderRadius: '0 8px 8px 0',
                                                fontSize: '20px',
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--main-primary, rgba(24, 144, 255, 0.2))';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))';
                                            }}
                                        >
                                            🎲
                                        </span>
                                    </Input.Group>
                                </div>
                            </div>

                            <div className="input-field">
                                <label>{language['input.creator.label'] || 'Copyright'}</label>
                                <Input
                                    value={localCreator}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setLocalCreator(newValue);
                                        debouncedCreatorUpdater(newValue);
                                    }}
                                    placeholder="©2026 TCGMaker.com"
                                />
                            </div>

                            {/* First Edition Text */}
                            <div className="input-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Checkbox
                                        checked={isFirstEdition}
                                        onChange={(e) => {
                                            setCard(card => ({
                                                ...card,
                                                isFirstEdition: e.target.checked,
                                            }));
                                        }}
                                    />
                                    {language['input.1st-edition.label'] || 'First Edition Text'}
                                    <Dropdown
                                        placement="bottomLeft"
                                        overlay={
                                            <Menu>
                                                {editionList.map(({ label, value }, index) => (
                                                    <Menu.Item
                                                        key={index}
                                                        onClick={() => {
                                                            setCard(card => ({
                                                                ...card,
                                                                isFirstEdition: true,
                                                                firstEditionText: value,
                                                            }));
                                                        }}
                                                    >
                                                        {label}
                                                    </Menu.Item>
                                                ))}
                                            </Menu>
                                        }
                                    >
                                        <UnorderedListOutlined
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: 'var(--main-primary)',
                                            }}
                                        />
                                    </Dropdown>
                                </label>
                                <Input
                                    value={localFirstEdition}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setLocalFirstEdition(newValue);
                                        debouncedFirstEditionUpdater(newValue);
                                    }}
                                    placeholder={language['input.1st-edition.placeholder'] || '1st Edition'}
                                    disabled={!isFirstEdition}
                                />
                            </div>

                            {/* Corner Text */}
                            <div className="input-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Checkbox
                                        checked={hasCornerText}
                                        onChange={(e) => {
                                            setCard(card => ({
                                                ...card,
                                                hasCornerText: e.target.checked,
                                            }));
                                        }}
                                    />
                                    {language['input.corner.label'] || 'Corner Text'}
                                </label>
                                <Input
                                    value={localCornerText}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setLocalCornerText(newValue);
                                        debouncedCornerTextUpdater(newValue);
                                    }}
                                    placeholder={language['input.corner.placeholder'] || 'Enter corner text (e.g., Limited Edition)'}
                                    disabled={!hasCornerText}
                                />
                            </div>

                            {/* Sticker */}
                            <div className="input-field">
                                <label>{language['input.sticker.label'] || 'Sticker'}</label>
                                <Radio.Group
                                    value={sticker}
                                    onChange={(e) => getUpdater('sticker')(e.target.value)}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                                        gap: '8px',
                                        width: '100%',
                                    }}
                                >
                                    {StickerButtonList.map(({ label, value }) => (
                                        <Radio.Button
                                            key={value}
                                            value={value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '56px',
                                                padding: '8px',
                                                borderRadius: '8px',
                                                border: '2px solid var(--main-border, rgba(255, 255, 255, 0.1))',
                                                background: value === sticker
                                                    ? 'var(--main-primary, rgba(24, 144, 255, 0.2))'
                                                    : 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                height: '100%',
                                                fontSize: value === NO_STICKER ? '20px' : '12px',
                                            }}>
                                                {label}
                                            </div>
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </div>
                        </Space>
                    </div>
                </TabPane>
            </Tabs>

            {/* Action Buttons - Below tabs */}
            <div className="action-buttons-sidebar">
                <button
                    className="action-tab-button download-tab-button"
                    onClick={onDownload}
                >
                    <DownloadOutlined />
                    <span>DOWNLOAD</span>
                </button>
                <GalleryCartActions />
            </div>
            </div>
            </MobileTabScroller>
        </div>
    );
});

// Simplified Import Tab Component - Inline import without modal
type SimplifiedImportTabProps = {
    language: any;
    onImport: (
        data: Card,
        option?: {
            forcePurityCheck?: boolean,
            writeOnCurrentCard?: boolean,
            addToList?: boolean,
        },
    ) => void;
    allowHotkey: boolean;
};

const SimplifiedImportTab: React.FC<SimplifiedImportTabProps> = ({ language, onImport, allowHotkey }) => {
    const [loading, setLoading] = useState(false);
    const [importMode] = useState<'replace' | 'merge' | 'new'>('replace');
    const ygoCarderImportInputRef = useRef<HTMLInputElement>(null);
    const [inputKey, setInputKey] = useState(0);

    const startImport = async (
        cardData: string | Record<string, any> | null,
        imageSurvey = false,
    ) => {
        try {
            if (cardData) {
                const { decodeCard } = await import('../../service');
                const {
                    card: decodedCard,
                    isPartial,
                } = decodeCard(
                    cardData,
                    (importMode === 'replace' || importMode === 'new') ? undefined : useCard.getState().card,
                );

                if (isPartial) {
                    const { notification } = await import('antd');
                    notification.info({
                        message: language['service.decode.partial.message'],
                        description: language['service.decode.partial.description'],
                    });
                }

                onImport(decodedCard, {
                    forcePurityCheck: true,
                    writeOnCurrentCard: importMode === 'new' ? false : true,
                    addToList: importMode === 'new',
                });
            }
        } catch (e) {
            console.error('Import error:', e);
            const { notification } = await import('antd');
            notification.error({
                message: language['error.import.error.message'],
                description: language['error.import.error.description'],
            });
        }
    };

    const handleCardSelect = async (card: any) => {
        setLoading(true);
        try {
            const { ygoproDeckToYgoCarderData } = await import('../../util/codec-ygoprodeck');
            const { result } = ygoproDeckToYgoCarderData(card);
            
            await startImport(result, false);
            
            const { notification } = await import('antd');
            notification.info({
                message: language['prompt.import.cors.info.message'] || 'Card imported successfully',
                description: language['prompt.import.cors.info.description'] || 'Note: To download this card, right-click on it and select "Save image as..." due to image hosting restrictions.',
                duration: 6,
            });
            
            setTimeout(() => {
                setLoading(false);
            }, 100);
        } catch (error) {
            console.error('Import error:', error);
            setLoading(false);
        }
    };

    const getFileAndImport = (fileList?: FileList | null) => {
        if (fileList) {
            for (let cnt = 0; cnt <= fileList.length; cnt++) {
                const targetFile = fileList.item(cnt);
                if (!targetFile) continue;

                const reader = new FileReader();
                reader.onload = ({ target }) => {
                    if (!target) return;

                    const { result } = target;
                    if (typeof result !== 'string') return;
                    startImport(result);
                };
                reader.readAsText(targetFile);
                break;
            }
        }
    };

    const startRequest = async () => {
        try {
            const target = document.getElementById('import-textarea-inline') as HTMLInputElement;
            if (target) {
                const { value } = target;
                const normalizedValue = value.trim() ?? '';
                let uncrushedValue = '';
                try {
                    const { JSONUncrush } = await import('../../3rd');
                    uncrushedValue = JSONUncrush(decodeURIComponent(normalizedValue));
                } catch (e) {}
                let cardData: string | null | Record<string, any> = null;

                if (normalizedValue.startsWith('{') && normalizedValue.endsWith('}')) {
                    cardData = normalizedValue;
                }
                else if (uncrushedValue.startsWith('{') && uncrushedValue.endsWith('}')) {
                    cardData = JSON.parse(uncrushedValue);
                }
                else {
                    const ygoproDeckApi = normalizedValue.startsWith('https://db.ygoprodeck.com/api')
                        ? normalizedValue
                        : `https://db.ygoprodeck.com/api/v7/cardinfo.php?fname=${normalizedValue}&num=10&offset=0&misc=yes`;
                    setLoading(true);
                    const response = await fetch(ygoproDeckApi);
                    if (!response.ok) {
                        throw new Error(`Response status: ${response.status}`);
                    }

                    const baseCardData: { data: any[] } = await response.json();
                    cardData = baseCardData.data.find(({ name }: any) => name.toLowerCase() === normalizedValue.toLowerCase())
                        ?? baseCardData.data.sort((l: any, r: any) => {
                            const lMisc = l.misc_info?.[0];
                            const rMisc = r.misc_info?.[0];

                            if (!lMisc || !rMisc) return 0;

                            const viewWeekRatio = (lMisc.viewsweek + 1) / (rMisc.viewsweek + 1);
                            if (viewWeekRatio > 2) return rMisc.viewsweek - lMisc.viewsweek;

                            return rMisc.views - lMisc.views;
                        })[0];
                }

                await startImport(cardData, true);
            }
        } catch (e) {
            console.error('Import error:', e);
            const { notification } = await import('antd');
            notification.error({
                message: language['error.import.error.message'],
                description: language['error.import.error.description'],
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="input-section">
            <h3 className="section-title">
                <UploadOutlined /> {language['generic.import.label'] || 'Import Card'}
            </h3>
            
            <Space direction="vertical" style={{ width: '100%' }} size="middle">

                {/* Card Search */}
                <div className="input-field">
                    <label>{language['prompt.import.search.label'] || 'Search for a card'}</label>
                    <CardSearch
                        onSelect={handleCardSelect}
                        placeholder={language['prompt.import.search.placeholder'] || 'Search for a Yu-Gi-Oh card...'}
                        disabled={loading}
                    />
                </div>

                {/* Divider */}
                <div style={{
                    textAlign: 'center',
                    margin: '8px 0',
                    position: 'relative',
                }}>
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '50%',
                        height: '1px',
                        background: 'var(--main-border, rgba(255, 255, 255, 0.1))',
                    }} />
                    <span style={{
                        position: 'relative',
                        background: 'var(--main-background-secondary, rgba(26, 26, 26, 0.6))',
                        padding: '0 12px',
                        color: 'var(--main-text-secondary)',
                        fontSize: '12px',
                    }}>
                        {language['prompt.import.alternative.label'] || 'OR'}
                    </span>
                </div>

                {/* Raw Data Input */}
                <div className="input-field">
                    <label>{language['prompt.import.message'] || 'Paste card data or URL'}</label>
                    <TextArea
                        key={`input-${inputKey}`}
                        id="import-textarea-inline"
                        placeholder={language['prompt.import.message']}
                        disabled={loading}
                        onPressEnter={startRequest}
                        rows={4}
                    />
                    <button
                        style={{
                            marginTop: '8px',
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '2px solid var(--main-primary, rgba(24, 144, 255, 0.6))',
                            background: 'var(--main-primary, rgba(24, 144, 255, 0.2))',
                            color: 'var(--main-text-primary, #fff)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                        }}
                        onClick={startRequest}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : (language['prompt.import.ok.label'] || 'Import')}
                    </button>
                </div>

                {/* File Upload */}
                {importMode !== 'merge' && (
                    <div className="input-field">
                        <label>{language['button.import.tooltip'] || 'Upload JSON File'}</label>
                        <button
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '2px solid var(--main-border, rgba(255, 255, 255, 0.1))',
                                background: 'var(--main-background-tertiary, rgba(13, 13, 13, 0.8))',
                                color: 'var(--main-text-primary, #fff)',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'all 0.3s ease',
                            }}
                            onClick={() => {
                                const target = document.getElementById('upload-inline');
                                if (target) {
                                    target.click();
                                }
                            }}
                        >
                            <UploadOutlined /> {language['button.import.tooltip'] || 'Upload Card'}
                        </button>
                        <input
                            key={`file-input-${inputKey}`}
                            ref={ygoCarderImportInputRef}
                            type="file"
                            id="upload-inline"
                            accept="application/json"
                            style={{ display: 'none' }}
                            onChange={() => {
                                const fileList = ygoCarderImportInputRef.current?.files;
                                getFileAndImport(fileList);
                                setInputKey(cur => cur + 1);
                            }}
                        />
                    </div>
                )}
            </Space>
        </div>
    );
};

export default SimplifiedInputPanel;
